import React from 'react';
import styled, { css, RuleSet } from 'styled-components';
import { globalSystemColors } from '../../styled/theme/constants';
import { lightColors } from '../../styled/theme/light';
import { IconColor, SVGComponent, IconProps } from './types';

type FillableColor = Exclude<
  IconColor,
  IconColor.strokeDark | IconColor.strokeDarkTan
>;

export const fillValues: Record<FillableColor, string> = {
  [IconColor.white]: globalSystemColors.white,
  [IconColor.blue]: globalSystemColors.blue,
  [IconColor.red]: '#ff3c00',
  [IconColor.pink]: lightColors.systemColors.lightRed,
  [IconColor.darkTan]: lightColors.systemColors.brand,
  [IconColor.brown]: globalSystemColors.heatheredGrey,
  [IconColor.dark]: globalSystemColors.cocoaBrown,
  [IconColor.darkRed]: '#a4243b',
  [IconColor.green]: globalSystemColors.green,
  [IconColor.deepDark]: globalSystemColors.cocoaBrown
};

const colorStyles: Record<IconColor, RuleSet> = {
  [IconColor.white]: css`
    fill: ${fillValues[IconColor.white]};
  `,
  [IconColor.blue]: css`
    fill: ${fillValues[IconColor.blue]};
  `,
  [IconColor.red]: css`
    fill: ${fillValues[IconColor.red]};
  `,
  [IconColor.pink]: css`
    fill: ${fillValues[IconColor.pink]};
  `,
  [IconColor.darkTan]: css`
    fill: ${fillValues[IconColor.darkTan]};
  `,
  [IconColor.brown]: css`
    fill: ${fillValues[IconColor.brown]};
  `,
  [IconColor.dark]: css`
    fill: ${fillValues[IconColor.dark]};
  `,
  [IconColor.darkRed]: css`
    fill: ${fillValues[IconColor.darkRed]};
  `,
  [IconColor.green]: css`
    fill: ${fillValues[IconColor.green]};
    stroke: ${fillValues[IconColor.green]};
  `,
  [IconColor.deepDark]: css`
    fill: ${fillValues[IconColor.deepDark]};
    stroke: ${fillValues[IconColor.deepDark]};
  `,
  [IconColor.strokeDark]: css`
    stroke-width: 1.5;
    path {
      stroke: ${globalSystemColors.cocoaBrown};
    }
  `,
  [IconColor.strokeDarkTan]: css`
    path {
      stroke: #ca1016;
    }
  `
};

interface StyledIconProps {
  $color: IconColor;
  $indent: boolean;
  $lgIndent: boolean;
  $pathColor?: IconColor;
  $iconNeedColorChange: boolean;
}

interface IconBaseProps extends StyledIconProps {
  IconComponent: SVGComponent;
  className?: string;
  width?: number;
  height?: number;
  onClick?: IconProps['onClick'];
  onKeyPress?: React.KeyboardEventHandler;
  'data-cy'?: string;
  'aria-label'?: string;
  role?: string;
  tabIndex?: number;
}

// Passes className directly to the SVG — no wrapper DOM element
const IconBase = ({
  IconComponent,
  className,
  $color: _color,
  $indent: _indent,
  $lgIndent: _lgIndent,
  $pathColor: _pathColor,
  $iconNeedColorChange: _iconNeedColorChange,
  ...props
}: IconBaseProps) =>
  React.createElement(IconComponent, { className, ...props });

export const StyledIcon = styled(IconBase)<StyledIconProps>`
  display: inline-block;
  vertical-align: middle;
  transform: translateZ(0);

  ${({ $color }) => colorStyles[$color]}

  ${({ $pathColor }) => {
    const fill =
      $pathColor && $pathColor === IconColor.white && fillValues[$pathColor];
    return (
      fill &&
      css`
        path {
          fill: ${fill};
        }
      `
    );
  }}

  ${({ $iconNeedColorChange }) =>
    $iconNeedColorChange &&
    css`
      path {
        fill: inherit;
      }
      rect[stroke],
      path[stroke] {
        stroke: inherit;
      }
    `}

  ${({ $indent, $lgIndent }) =>
    $indent &&
    css`
      margin-right: ${$lgIndent ? '10px' : '5px'};
    `}
`;
